import React, { useState, ReactElement, useEffect } from "react";
import { useAccount } from "wagmi";
import MarketSearchPage from "./MarketSearchPage";
import MarketConfirmPage from "./MarketConfirmPage";
import { MarketPaymentPage } from "./MarketPaymentPage";
import MarketLandingPage from "./MarketLandingPage";
import { Stripe } from "@stripe/stripe-js";
import { closeButton } from "./svgs";
import { v4 as uuidv4 } from "uuid";
import { MarketMintPage } from "./MarketMintPage";
import axios from "axios";

export interface MarketModalConfig {
  userInfo: {
    externalUserId: string;
    walletAddress: string;
    email?: string;
    fullName?: string;
  };
  cryptocurrencies: string[];
  apiUrl?: string;
  stripe: {
    stripe: Stripe | Promise<Stripe | null>;
    connectedAccountId: string;
  };
  demo: boolean;
  skipPayment: boolean;
  markets: string[];
  appearance?: {
    heroCardDomain: string;
    heroCardAvatarImageUrl: string;
    heroCardBackgroundImageUrl: string;
    heroCardShadowColor: string;
    primaryColor?: string;
    secondaryColor?: string;
    button: {
      background: string;
      boxShadow: string;
      borderRadius: number;
      border: string;
      color: string;
    };
  };
}

const defaultApiUrl = "https://api.superlink.me";

interface WalletConnectMarketModalProps extends MarketModalProps {
  initialNotConnected: () => void;
  setWalletAddr: (addr: string) => void;
}

interface MarketModalProps {
  onOpen: () => void;
  onClose: () => void;
  config: MarketModalConfig;
}

export interface MarketBasketItem {
  domain: string;
  nameService: string;
  price: Price;
  currency: string;
  region: string;
  years: number;
  features: string[];
}

export interface Price {
  ens_bridge: number;
  ens_rent: number;
  ens_setup_gas: number;
  renewal: number;
  setup: number;
}

const ConnectedMarketModal: React.FC<WalletConnectMarketModalProps> = ({
  onOpen,
  onClose,
  initialNotConnected,
  setWalletAddr,
  config,
}) => {
  const [isReady, setIsReady] = useState(false);
  const { address, isConnected } = useAccount({
    onConnect: ({ address, connector, isReconnected }) => {
      console.log(`connected!`, address);
      setWalletAddr(address ? address : "0xDEADBEEF");
    },
    onDisconnect: () => {
      console.log(`disconnected!`);
      setWalletAddr("0xDEADBEEF");
    },
  });

  useEffect(() => {
    setTimeout(function () {
      setIsReady(true);
    }, 500);
    if (!isConnected) {
      initialNotConnected();
    }
  }, []);

  return (
    <>
      {isReady && isConnected ? (
        <MarketModal onOpen={onOpen} onClose={onClose} config={config} />
      ) : null}
    </>
  );
};

const MarketModal: React.FC<MarketModalProps> = ({
  onOpen,
  onClose,
  config,
}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [basket, setBasket] = useState<MarketBasketItem>();
  const [query, setQuery] = useState<string>("");
  const [result, setResult] = useState<MarketBasketItem[]>([]);
  const [checkoutId, setCheckoutId] = useState<string>("");
  const [estimate, setEstimate] = useState<any>([]);
  const [seconds, setSeconds] = useState<number>(30);
  const [currency, setCurrency] = useState<string>("USD");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.matchMedia("(max-width: 768px)").matches);
  const [opacity, setOpacity] = useState(isMobile ? 1.0:  0.0);
  const [modalSize, setModalSize] = useState<{width: any, height: any}>({ width: 350, height: isMobile ? 0:  590}); // Default size
  const [tSpeed, setTSpeed] = useState(0.3);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 420px)");
    const handleResize = () => setIsMobile(mediaQuery.matches);

    mediaQuery.addListener(handleResize);
    return () => mediaQuery.removeListener(handleResize);
  }, []);

  useEffect(() => {
    if (!isModalOpen) {
      handleOpenModal()
    }
  }, []);

  const handleOpenModal = () => {
    setIsModalOpen(true);
    onOpen();
    if(isMobile) {
      setModalSize({...modalSize, height: 0})
    } else {
      setTimeout(() => {
        setOpacity(1.0);
      }, 200);
    }

  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    onClose();
  };

  useEffect(() => {
    if(config.cryptocurrencies.length > 0) fetchEstimate();
  }, []);

  const fetchEstimate = () => {
    const headers = {
      "Content-Type": "application/json",
    };

    axios
      .get(`${config.apiUrl || defaultApiUrl}/v1/market/crypto/estimate`, {
        headers,
      })
      .then((response) => {
        setEstimate(
          [{ symbol: "USD", name: "United States Dollar", exhange: 1 }].concat(
            response.data.estimations
          )
        );
      });
  };

  useEffect(() => {
    const intervalTime = 1000;

    const interval = setInterval(() => {
      setSeconds((seconds) => {
        if (seconds <= 1 && currentPage < 4) {
          if(config.cryptocurrencies.length > 0) fetchEstimate();
        }
        if (seconds <= 0) {
          return 30;
        }

        return seconds - 1;
      });
    }, intervalTime);

    // Clean up the interval when the component is unmounted
    return () => {
      clearInterval(interval);
    };
  }, [currentPage]);

  const addToBasket = (item: MarketBasketItem) => {
    setBasket(item);
    handleNext();
  };

  const finish = () => {
    closeModal()
  };

  const submitQuery = (query: string) => {
    setQuery(query);
  };

  const increaseYears = () => {
    if (!basket || basket.years >= 10) {
      return;
    }

    setBasket({
      ...basket,
      years: basket.years + 1,
    });
  };

  const decreaseYears = () => {
    if (!basket || basket.years <= 1) {
      return;
    }

    setBasket({
      ...basket,
      years: basket.years - 1,
    });
  };

  const closeModal = () => {
    if(isMobile) {
      setModalSize({...modalSize, height: 0})
    } else {
      setOpacity(0)
    }
    setTimeout(() => {
      handleCloseModal();
      setBasket(undefined);
      setQuery("");
      setCurrentPage(0);
    }, 300);
  };

  const submitInitialQuery = () => {
    setResult([]);
    setCurrentPage(currentPage + 1);
    setCurrentPage((x) => {
      return x
    })
  };

  const handleNext = () => {
    if (currentPage < pages.length - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrev = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handler = (touchEvent: any) => {
    setTSpeed(0.0)
    function onTouchMove(touchMoveEvent: any) {
      setModalSize((_) => { 
        console.log(touchMoveEvent)
        return {
          width: window.innerHeight - touchMoveEvent.touches[0].clientX, 
          height: window.innerHeight - touchMoveEvent.touches[0].clientY 
        }
      });
    }
    function onTouchEnd() {
      setTSpeed(0.3)
      document.body.removeEventListener("touchmove", onTouchMove);
    }
    
    document.body.addEventListener("touchmove", onTouchMove);
    document.body.addEventListener("touchend", onTouchEnd, { once: true });
  };

  const pages: ReactElement[] = [
    <MarketLandingPage
      submit={submitInitialQuery}
      setQuery={submitQuery}
      query={query}
      userInfo={config.userInfo}
      setHeight={(h: any) => {
        setModalSize({...modalSize, height: h})
      }}
    />,
    <MarketSearchPage
      addToBasket={addToBasket}
      setQuery={submitQuery}
      result={result}
      setResult={setResult}
      query={query}
      userInfo={config.userInfo}
      markets={config.markets}
      apiUrl={config.apiUrl || defaultApiUrl}
      currencyEstimates={estimate}
      currencies={config.cryptocurrencies}
      currency={currency}
      setCurrency={setCurrency}
      back={handlePrev}
      seconds={seconds}
      setHeight={(h: any) => {
        setModalSize({...modalSize, height: h})
      }}
    />,
    <MarketConfirmPage
      basket={basket}
      userInfo={config.userInfo}
      confirm={() => {
        // TODO temporarily bypass payment screen
        setCurrentPage(currentPage + 1);
      }}
      increaseYears={increaseYears}
      decreaseYears={decreaseYears}
      currencyEstimates={estimate}
      back={handlePrev}
      seconds={seconds}
      setHeight={(h: any) => {
        setModalSize({...modalSize, height: h})
      }}
    />,
    <MarketPaymentPage
      basket={basket}
      currencyEstimates={estimate}
      domain={basket?.domain || ""}
      stripe={config.stripe}
      userInfo={config.userInfo}
      apiUrl={config.apiUrl || defaultApiUrl}
      checkoutId={checkoutId}
      demo={config.demo}
      skipPayment={config.skipPayment}
      setCheckoutId={(checkoutId: string) => {
        localStorage.setItem("checkoutId", checkoutId);
        setCheckoutId(checkoutId);
      }}
      back={handlePrev}
      next={handleNext}
      setHeight={(h: any) => {
        setModalSize({...modalSize, height: h})
      }}
    />,
    <MarketMintPage
      domain={basket?.domain || ""}
      apiUrl={config.apiUrl || defaultApiUrl}
      userInfo={config.userInfo}
      checkoutId={checkoutId}
      walletAddress={config.userInfo.walletAddress}
      close={finish}
      setHeight={(h: any) => {
        setModalSize({...modalSize, height: h})
      }}
    />,
  ];

  return (
    <>
      <div>
        {!isModalOpen ? (
          <button onClick={handleOpenModal}>Open Modal</button>
        ) : null}
      </div>
      {isModalOpen && (
        <div style={{...modalStyle(isMobile, modalSize.height != 0), opacity: opacity, transition: 'opacity 0.3s'}}>
          <button style={modalCloseButtonStyle} onClick={closeModal}>
            {closeButton}
          </button>
          {/* <button style={{position: 'relative', opacity: 0, width: "100%", height: "10px"}} onTouchStart={handler} /> */}
          <div  style={modalBodyStyle(isMobile, modalSize, tSpeed)}>{pages[currentPage]}</div>
        </div>
      )}
    </>
  );
};

const modalStyle = (isMobile: boolean, dark: boolean): React.CSSProperties => ({
  position: "fixed",
  gap: 10,
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: dark ? "rgba(0, 0, 0, 0.6)": 'rgba(0, 0, 0, 0.0)',
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: isMobile ? "end": "center",
})

const modalBodyStyle = (isMobile: boolean, modalSize: any, tSpeed: any): React.CSSProperties => ({
  borderTopLeftRadius: 24,
  borderTopRightRadius: 24,
  borderBottomLeftRadius: isMobile ? 0: 24,
  borderBottomRightRadius: isMobile ? 0: 24,
  maxHeight: 800,
  minHeight: 0,
  width: "100%",
  maxWidth: 420,
  minWidth: 250,
  height: modalSize.height,
  transition: `width ${tSpeed}s linear, height ${tSpeed}s linear`,
  backgroundColor: "rgba(248, 248, 248, 1)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  overflow: "hidden",
});

const modalPageStyle: React.CSSProperties = {
  width: "100%",
  height: "100%",
};

const modalCloseButtonStyle: React.CSSProperties = {
  border: "none",
  backgroundColor: "transparent",
  outline: "none",
  cursor: "pointer",
  paddingTop: 20
};

export default ConnectedMarketModal;
